import {Injectable, Optional} from '@angular/core';
import {EformDataService} from "./eform-data.service";
import {EformDuplicableFieldsService} from "./eform-duplicable-fields.service";
import {EformDuplicableEntityService} from "./eform-duplicable-entity.service";

@Injectable({
  providedIn: 'root'
})
export class EformConditionsService {

  private existingItemIds: string[] = [];

  constructor(private eformDataService: EformDataService,
              private eformDuplicableFieldsService: EformDuplicableFieldsService,
              @Optional() private eformDuplicableEntityService?: EformDuplicableEntityService) {
  }

  /**
   * Vide la liste d'elements existant
   */
  clearExistingItemsList() {
    this.existingItemIds = [];
  }

  clearExistingItemsListById(id: string) {
    this.existingItemIds = this.existingItemIds.filter(e=>e !== id);
  }

  /**
   * Vide les éléments existants pour un contexte parent spécifique
   */
  clearExistingItemsForParentContext(parentContext: string) {
    if (parentContext) {
      this.existingItemIds = this.existingItemIds.filter(id => !id.startsWith(parentContext + '::'));
    }
  }

  /**
   * Ajoute un element à la liste des objets deja créé en verifiant s'il n'y est pas deja
   */
  addElementToExistingItemsList(element){
    if (!this.existingItemIds.includes(element)) {
      this.existingItemIds.push(element);
    }
  }

  /**
   * Extrait le contexte du parent duplicable à partir du repeatablePathWithIndex
   */
  extractParentDuplicableContext(repeatablePathWithIndex: string): string {
    if (!repeatablePathWithIndex) {
      return '';
    }

    // ✅ FIX 2026-01-08: Liste des éléments duplicables "majeurs" qui sont chargés un par un
    const majorDuplicableElements = [
      'organization',
      'lotresult',
      'lottender',
      'tenderingparty',
      'settledcontract',
      'procurementprojectlot'
    ];

    // D'abord, chercher le pattern classique avec _INDEX_
    for (const element of majorDuplicableElements) {
      const regex = new RegExp(`${element}\\[\\](?:\\.val)?_INDEX_(\\d+)`, 'i');
      const match = repeatablePathWithIndex.match(regex);
      if (match) {
        return `${element}_${match[1]}`;
      }
    }

    // Si pas trouvé, chercher si un élément duplicable est présent dans le chemin
    // et utiliser actuaEntityIndex comme fallback
    for (const element of majorDuplicableElements) {
      if (repeatablePathWithIndex.toLowerCase().includes(element + '[]')) {
        const index = this.eformDuplicableEntityService?.actuaEntityIndex;
        if (index !== undefined && index >= 0) {
          return `${element}_${index}`;
        }
      }
    }

    return '';
  }

  /**
   * 🔧 FIX 2026-02-15: Vérifie si un élément répétable est imbriqué dans un parent duplicable majeur.
   * Utilisé pour autoriser la création d'éléments répétables (comme tenderer[] dans tenderingparty[])
   * pendant le rechargement du parent duplicable.
   *
   * @param jsonPath Le chemin JSON de l'élément répétable
   * @returns true si l'élément est imbriqué dans un parent duplicable
   */
  private isNestedInDuplicableParent(jsonPath: string): boolean {
    if (!jsonPath) return false;

    const lowerPath = jsonPath.toLowerCase();

    // Liste des parents duplicables majeurs
    const duplicableParentPatterns = [
      'lotresult[]',
      'organization[]',
      'lottender[]',
      'settledcontract[]',
      'tenderingparty[]',
      'procurementprojectlot[]'
    ];

    // Vérifier si le jsonPath contient un parent duplicable ET que l'élément répétable
    // est APRÈS ce parent (donc c'est un enfant du parent)
    for (const pattern of duplicableParentPatterns) {
      if (lowerPath.includes(pattern)) {
        // Vérifier que ce n'est pas l'élément duplicable lui-même mais un enfant
        // L'élément est un enfant s'il y a d'autres segments après le parent duplicable
        const parentIndex = lowerPath.indexOf(pattern);
        const afterParent = lowerPath.substring(parentIndex + pattern.length);

        // S'il y a d'autres segments (autres que .val), c'est un élément imbriqué
        if (afterParent && afterParent !== '.val' && afterParent.length > 4) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * ✅ FIX 2026-01-08: Vérifie si un élément répétable est imbriqué dans un parent duplicable qui n'est pas encore chargé.
   *
   * Les éléments duplicables (RES, ORG, TEN, CON, TPA) sont chargés un par un depuis le backend.
   * Avant le chargement, le tableau correspondant dans noticeValue est vide.
   * Dans ce cas, on ne doit pas créer d'éléments répétables enfants.
   *
   * @param jsonPath Le chemin JSON de l'élément répétable
   * @param noticeValue La valeur de la notice
   * @returns true si le parent duplicable n'est pas encore chargé
   */
  private isNestedInUnloadedDuplicableParent(jsonPath: string, noticeValue: any): boolean {
    if (!jsonPath || !noticeValue) return false;

    const lowerPath = jsonPath.toLowerCase();

    // Liste des parents duplicables et leur chemin dans noticeValue
    // Chaque entrée peut avoir plusieurs chemins alternatifs (alternativePaths).
    // Le parent est considéré "chargé" si AU MOINS UN chemin pointe vers un tableau non vide.
    const duplicableParents: { pattern: string, alternativePaths: string[][] }[] = [
      {
        pattern: 'lotresult[]',
        alternativePaths: [['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lotresult']]
      },
      {
        pattern: 'organization[]',
        alternativePaths: [['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'organizations', 'organization']]
      },
      {
        pattern: 'lottender[]',
        alternativePaths: [
          ['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lottender'],
          // ✅ FIX 2026-03-24: Quand lottender[] est imbriqué dans lotresult[] (GR-LotResult-Tender-Id-Ref),
          // les données sont dans lotresult[0].lottender, pas dans le lottender de niveau supérieur
          ['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lotresult', '0', 'lottender']
        ]
      },
      {
        pattern: 'settledcontract[]',
        alternativePaths: [
          ['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'settledcontract'],
          // ✅ FIX 2026-03-24: Quand settledcontract[] est imbriqué dans lotresult[] (GR-LotResult-Contract-Id-Ref),
          // les données sont dans lotresult[0].settledcontract, pas dans le settledcontract de niveau supérieur
          ['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lotresult', '0', 'settledcontract']
        ]
      },
      {
        pattern: 'tenderingparty[]',
        alternativePaths: [['contractawardnotice', 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'tenderingparty']]
      },
      {
        // 🔧 FIX F16: procurementprojectlot[] existe dans contractawardnotice (F29) ET contractnotice (F16)
        // ET priorinformationnotice. Le parent est chargé si AU MOINS UN chemin est valide.
        pattern: 'procurementprojectlot[]',
        alternativePaths: [
          ['contractawardnotice', 'procurementprojectlot'],
          ['contractnotice', 'procurementprojectlot'],
          ['priorinformationnotice', 'procurementprojectlot']
        ]
      }
    ];

    // Vérifier si le jsonPath contient un parent duplicable
    for (const parent of duplicableParents) {
      if (lowerPath.includes(parent.pattern)) {
        // Le parent est chargé si AU MOINS UN chemin alternatif pointe vers un tableau non vide
        const isLoaded = parent.alternativePaths.some(pathToCheck => {
          let current: any = noticeValue;
          for (const key of pathToCheck) {
            if (!current) return false;
            current = current[key];
          }
          return current != null && Array.isArray(current) && current.length > 0;
        });

        if (!isLoaded) {
          return true; // Aucun chemin valide → parent non chargé
        }
      }
    }

    return false;
  }

  /**
   * Récupére les éléments d'une liste en fitltrant sur les conditions à partir de la noticeValue (plutot à utiliser en édition)
   * @param elementToVerify
   * @param elements
   * @param repeatablePathWithIndex
   * @private
   */
  filterWithConditions(elementToVerify, elements, repeatablePathWithIndex, noticeValue, elementToCreate) {
    if (!elements?.length) {
      return elementToCreate;
    }

    const actualId = elementToVerify.id?.includes('_') ? elementToVerify.id.split('_')[0] : elementToVerify.id;

    // ✅ FIX 2026-01-08: Pour les éléments imbriqués dans des éléments répétables (comme subordinateawardingcriterion
    // dans awardingcriterion), on ne peut pas vérifier les conditions correctement car le repeatablePathWithIndex
    // ne contient pas les bons index pour tous les niveaux d'imbrication.
    // Si l'élément existe dans noticeValue (via getFormArrayFromNoticeValueWithContext), c'est qu'il est valide.
    const indexMatches = repeatablePathWithIndex.match(/\[\](?:\.val)?_INDEX_\d+/gi) || [];
    const nestingLevel = indexMatches.length;

    // Si plus d'un niveau d'imbrication ET l'élément a des conditions, on ne filtre pas
    if (nestingLevel > 1 && elementToVerify.conditionsCreationMap) {
      return elementToCreate;
    }

    elements.forEach((grp, index) => {
      const elementId = actualId + '_' + index;

      if (!elementToCreate.includes(elementId)) {
        return;
      }

      let lastIndex = repeatablePathWithIndex.lastIndexOf('INDEX_') + 6;
      let repeatableWithoutLastIndex = repeatablePathWithIndex.slice(0, lastIndex);
      const updatedRepeatablePath = repeatableWithoutLastIndex + index;

      const conditionsOk = this.verifyConditionsWithNoticeValue(noticeValue, elementToVerify, updatedRepeatablePath);


      if (!conditionsOk) {
        elementToCreate = elementToCreate.filter(e => e !== elementId);
      }
    });
    return elementToCreate;
  }


  /**
   * Vérification des conditions qui contiennent 'text()'
   * @param obj
   * @param elementToVerify
   * @param condition
   * @param repeatablePathWithIndex
   * @private
   */
  private verifyConditionCreationWithText(obj, condition, repeatablePathWithIndex, conditionKey) {
    let canCreate = true;

    let isNot = condition.includes('not');
    if (isNot) {
      condition = condition.replace('not(', '').replace(')]', ']');
    }
    let completePath = condition.split('=')[0].replace('[', '').replace('text()', 'val').toLowerCase().split('/');
    let firstPartOfPath = [];
    if (repeatablePathWithIndex.includes('_SEPSEP_')) {
      let pathWithIndex = repeatablePathWithIndex.split('_SEPSEP_').filter(path => path.includes(conditionKey))[0];
      firstPartOfPath = pathWithIndex.split('[]_INDEX')[0].split('.').reverse();
    } else {
      firstPartOfPath = repeatablePathWithIndex.substring(0, repeatablePathWithIndex.lastIndexOf('[]')).split('.').reverse();
    }
    firstPartOfPath?.forEach(part => completePath.unshift(part));
    let values = [];
    if (condition.includes(',') && completePath) {
      values = condition.split('=')[1].replace(']', '').replace('(', '').replace(')', '').replaceAll('\'', '').split(',');
    } else {
      values.push(condition.split('=')[1].replace(']', '').replaceAll('\'', ''));
    }

    let tempObj = obj;
    completePath.forEach(element => {
      if (Array.isArray(tempObj) && tempObj?.length != 0) {
        tempObj = tempObj[this.eformDataService.getArrayIndex(element, repeatablePathWithIndex)]?.[element];
      } else if (tempObj?.[element]) {
        tempObj = tempObj?.[element];
      }
    });

    values.forEach(value => {
      if (isNot) {
        canCreate = canCreate && tempObj !== value;
      } else {
        canCreate = canCreate && tempObj === value;
      }
    });
    return canCreate;
  }

  /**
   * Vérifie les conditions à partir de l'objet noticeValue (plutot à utiliser en édition)
   * @param noticeValue
   * @param elementToVerify
   * @param repeatablePathWithIndex
   */
  verifyConditionsWithNoticeValue(noticeValue, elementToVerify, repeatablePathWithIndex) {

    let canCreate = true;

    //les conditions sont regroupées par champ associé à vérifier, on boucle pour tout vérifier
    if (elementToVerify.conditionsCreationMap) {
      Object.keys(elementToVerify.conditionsCreationMap).forEach(conditionKey => {
        let conditions = elementToVerify.conditionsCreationMap[conditionKey];

        //on recupére toutes les conditions pour un champ
        for (let i = 0; i < conditions.length; i++) {
          const condition = conditions[i];
          let noticeValueWithCondition = this.getNoticeValueWithCondition(noticeValue, elementToVerify, conditionKey, repeatablePathWithIndex)
          if (condition.includes('text()')) {
            canCreate = this.verifyConditionCreationWithText(noticeValueWithCondition, condition, repeatablePathWithIndex, conditionKey);
          } else if (condition.match(/\[/g)?.length == 1) {
            //si il n'y a qu'un seul [] , 1 seule étape
            let tempObj = noticeValueWithCondition;
            let isNot = condition.includes('not');
            let paths = condition.replace('not(', '').replace(')]', '').split('/');
            let index = 0;
            for (let j = 0; j < paths.length; j++) {
              const path = paths[j];
              if (index < paths.length - 1) {
                if (Array.isArray(tempObj) && tempObj?.length === 1) {
                  tempObj = tempObj[0]?.[path.replace('[', '').toLowerCase()];
                } else if(Array.isArray(tempObj) && tempObj?.length > 1){
                  const subconditions = [];
                  for (const iterator of conditions) {
                    subconditions.push({content : iterator});
                  }
                  tempObj = tempObj[this.findIndexWithSubCondition(subconditions,tempObj)];
                } else {
                  tempObj = tempObj?.[path.replace('[', '').toLowerCase()];
                }
              }
              if (index == paths.length - 1) {
                if (isNot && !path.includes('&')) {
                  let fieldShouldNotHave = [];
                  //Dans ce cas on a plusieurs vérification a effectuer
                  if (path.includes(',')) {
                    let valuesToNotMatch = path.replace('=(', '').replace(')', '').replaceAll('\'', '').split(',');
                  } else {
                    let fieldShouldNotHave = path.replace('[not(', '').replace(')]', '').replace('[', '').toLowerCase();
                    // verifier si control a l'item , si oui : return false !
                    canCreate = canCreate && !tempObj[fieldShouldNotHave];
                  }

                } else {
                  let attributeAndValue = path.slice(path.lastIndexOf('[') + 1, path.indexOf(']')).replace('&', '').split("=");
                  // verifier si le control a le listname , si oui : return false !
                  canCreate = canCreate && tempObj != null && tempObj != undefined && tempObj[attributeAndValue[0].toLowerCase()] == attributeAndValue[1].replaceAll('\'', '');
                }
              }
              index++;
            }
          }
          // si il y a plusieurs [] alors il y a des étapes supplémentaires pour trouver l'endroit de la condition
          else if (condition.match(/\[/g)?.length > 1) {
            let arrayOfCondition = condition.split('[').filter(n => n);
            let tempObj = noticeValueWithCondition;
            let shouldBreak = false;
            let isNot = false;
            arrayOfCondition.forEach((conditionInArray, index) => {
              // si il y a un not et pas d'attribut
              if (conditionInArray.includes('not(')) {
                isNot = true;
              }
              if (!conditionInArray.includes('&')) {
                let paths = conditionInArray.replace('not(', '').replace(')]', '').split('/');
                paths.forEach(path => {
                  if (Array.isArray(tempObj) && tempObj?.length != 0) {
                    tempObj = tempObj[this.eformDataService.getArrayIndex(elementToVerify, repeatablePathWithIndex)]?.[path.toLowerCase()];
                  } else {
                    tempObj = tempObj?.[path.toLowerCase()];
                  }
                })
              }
              if (!tempObj) {
                shouldBreak = true;
              }
              if (conditionInArray.includes('&') && !shouldBreak) {
                conditionInArray = conditionInArray.replace(']', '').replace(')', '').replace('(', '');
                let attributeAndValue = conditionInArray.replace('&', '').split("=");
                attributeAndValue[0] = attributeAndValue[0].toLowerCase();
                if (isNot) {
                  canCreate = canCreate && tempObj[attributeAndValue[0]] != attributeAndValue[1].replaceAll('\'', '').replaceAll(']', '');
                } else {
                  canCreate = canCreate && tempObj[attributeAndValue[0]] == attributeAndValue[1].replaceAll('\'', '').replaceAll(']', '');
                }
              }
            });
          }
        }
      });
    }
    return canCreate;
  }

  /**
   * Récupére la liste des éléments à créé en fonction des conditions (pour le cas de l'edition)
   * @param actualElement
   * @param formGroup
   * @param repeatablePathWithIndex
   * @param noticeValue
   */
  findElementsToCreate(actualElement, repeatablePathWithIndex, noticeValue) {
    // ✅ FIX 2025-12-24: Ne pas créer d'éléments répétables pendant le rechargement d'un élément duplicable
    // 🔧 FIX 2026-02-15: EXCEPTION pour les éléments répétables imbriqués dans un élément duplicable
    // Quand on recharge un TPA, les GR-Tenderer (tenderer[]) à l'intérieur doivent pouvoir être créés
    // car les données viennent d'être chargées dans noticeValue par loadTenderingParty
    // 🔧 FIX 2026-03-23: EXCEPTION pour les éléments répétables de niveau supérieur (non imbriqués
    // dans un élément duplicable). Ces éléments (ex: nomenclatures complémentaires au niveau Procedure)
    // sont indépendants du rechargement d'un élément duplicable et doivent pouvoir être recréés.
    // Sans cette exception, seul 1 groupe est recréé au lieu de N, puis une sauvegarde immédiate
    // écrase les N entrées du backend avec une seule.
    if (this.eformDuplicableFieldsService.isReloadingDuplicableElementValue) {
      // Vérifier si l'élément est un sous-élément répétable d'un élément duplicable
      // Si oui, on AUTORISE la création car les données sont fraîchement chargées
      const isNestedRepeatableInDuplicable = actualElement._repeatable &&
        this.isNestedInDuplicableParent(actualElement.jsonPath);

      // Vérifier si l'élément répétable est de niveau supérieur (NON imbriqué dans un duplicable)
      // Ces éléments sont indépendants et doivent toujours pouvoir être recréés
      const isTopLevelRepeatable = actualElement._repeatable &&
        !this.isNestedInDuplicableParent(actualElement.jsonPath);

      if (!isNestedRepeatableInDuplicable && !isTopLevelRepeatable) {
        return [];
      }
    }

    //Pour les elements répétable, il faut vérifier combien il y a d'occurences et toutes les régénérer
    if (actualElement._repeatable) {
      // ✅ FIX 2026-01-08: Vérifier si le parent duplicable est chargé dans noticeValue
      if (this.isNestedInUnloadedDuplicableParent(actualElement.jsonPath, noticeValue)) {
        return [];
      }

      // ✅ FIX 2026-01-09: Pour les éléments imbriqués dans des éléments duplicables majeurs,
      // utiliser un contexte STABLE basé sur le type d'élément duplicable actuel.
      // Cela évite les problèmes où le repeatablePathWithIndex varie entre les appels.
      const parentContext = this.getStableParentContext(actualElement.jsonPath);
      const contextPrefix = parentContext ? parentContext + '::' : '';

      // ✅ FIX: Utiliser getFormArrayFromNoticeValueWithContext pour récupérer uniquement les éléments du parent actuel
      let elements = this.eformDataService.getFormArrayFromNoticeValueWithContext(
        actualElement.jsonPath,
        noticeValue,
        repeatablePathWithIndex,
        true
      );

      //L'élement actuel est en cours de création, on l'ajoute à la liste des objets déjà créés s'il n'y est pas
      let actualIndex = +repeatablePathWithIndex.split('_')[repeatablePathWithIndex.split('_').length - 1];
      let actualId = actualElement.id?.includes('_') ? actualElement.id.split('_')[0] : actualElement.id;

      // ✅ FIX: Inclure le contexte parent dans l'ID complet
      let actualCompleteId = contextPrefix + actualId + '_' + actualIndex;
      if (!this.existingItemIds.includes(actualCompleteId)) {
        this.existingItemIds.push(actualCompleteId);
      }
      if (elements) {
        let elementToCreate: string[] = [];

        const elementsBeforeFieldFilter = Array.isArray(elements) ? [...elements] : elements;
        elements = this.filterElementWithSamePathButDifferentField(actualElement, elements);

        // ✅ FIX 2026-02-03: Filtrer par listname AVANT de compter les éléments
        // Cela évite de compter des éléments avec un listname différent (ex: environmental-impact vs green-procurement)
        if (actualElement.listname) {
          elements = elements.filter(element => element.listname === actualElement.listname);
        }

        elements.forEach((elem, index) => {
          // ✅ FIX: Utiliser le contexte parent pour les IDs
          const elementIdWithContext = contextPrefix + actualId + '_' + index;
          if (!this.existingItemIds.includes(elementIdWithContext)) {
            elementToCreate.push(actualId + '_' + index);
          }
        });

        //On filtre les elements qui ne remplissent pas les conditions
        elementToCreate = this.filterWithConditions(actualElement, elements, repeatablePathWithIndex, noticeValue, elementToCreate);


        //On ajoute chaque élément à la liste des éléments créer pour eviter les doubons
        // ✅ FIX 2026-01-08: Ajouter avec le contexte parent pour éviter les conflits entre RES
        elementToCreate?.forEach(element => {
          this.addElementToExistingItemsList(contextPrefix + element);
        });

        return elementToCreate;
      }
    }
  }

  /**
   * ✅ FIX 2026-01-09: Retourne un contexte parent STABLE basé sur le jsonPath de l'élément
   * et l'index de l'élément duplicable actuellement affiché (via actuaEntityIndex).
   * Cela garantit que tous les appels pour le même type d'élément duplicable utilisent le même contexte.
   */
  private getStableParentContext(jsonPath: string): string {
    if (!jsonPath) return '';

    const lowerPath = jsonPath.toLowerCase();
    const majorDuplicableElements = [
      { name: 'lotresult', pattern: 'lotresult[]' },
      { name: 'organization', pattern: 'organization[]' },
      { name: 'lottender', pattern: 'lottender[]' },
      { name: 'tenderingparty', pattern: 'tenderingparty[]' },
      { name: 'settledcontract', pattern: 'settledcontract[]' }
    ];

    for (const element of majorDuplicableElements) {
      if (lowerPath.includes(element.pattern)) {
        // Utiliser actuaEntityIndex pour identifier le bon élément duplicable
        const index = this.eformDuplicableEntityService?.actuaEntityIndex ?? 0;
        return `${element.name}_${index}`;
      }
    }

    return '';
  }

  /**
   * Méthode permettant de filtrer les éléments en fonction des ids des fields de actualElement.
   * En effet, à cause des elements différent mais ayant le même path, il faut filtrer ces derniers.
   * Pour les identifiés, on exclue les elements qui ne contiennent pas au moins un id des fields de actualElement.
   * Correspondance entre : id des fields, et inputId des elements.
   *
   * ✅ FIX 2026-02-04: Utilisation de `some` au lieu de `every` pour supporter les avis importés
   * qui n'ont pas toutes les structures initialisées (comme fieldsPrivacy, awardCriterionParameter, etc.)
   * Un élément est conservé s'il contient AU MOINS UN des inputIds attendus.
   *
   * @param actualElement Element actuel
   * @param elements Les éléments récupérés
   * @returns
   */
  private filterElementWithSamePathButDifferentField(actualElement, elements) {
    // Récupère les ids des fields si présents
    const fieldIds = Array.isArray(actualElement.fields)
      ? actualElement.fields.map(field => field.id).filter(id => !!id)
      : [];

    // Si aucun field.id, on garde tous les éléments
    if (fieldIds.length === 0) {
      return elements;
    }

    const elementHasFieldId = (obj: any, fieldId: string): boolean => {
      if (!obj) {
        return false;
      }
      const target = fieldId?.toLowerCase();
      let found = false;

      const traverse = (o: any) => {
        if (found || !o || typeof o !== 'object') {
          return;
        }
        for (const key in o) {
          if (!Object.prototype.hasOwnProperty.call(o, key)) {
            continue;
          }
          const value = o[key];

          // Check key name (in case some elements use the field id as a key)
          if (key && key.toLowerCase() === target) {
            found = true;
            return;
          }

          // Check common identifier fields and any string values
          if (typeof value === 'string') {
            const valLower = value.toLowerCase();
            if ((key.toLowerCase() === 'inputid' || key.toLowerCase() === 'listname' || key.toLowerCase() === 'id') && valLower === target) {
              found = true;
              return;
            }
            if (valLower === target) {
              found = true;
              return;
            }
          } else if (Array.isArray(value)) {
            for (const item of value) {
              traverse(item);
              if (found) { return; }
            }
          } else if (typeof value === 'object') {
            traverse(value);
            if (found) { return; }
          }
        }
      };

      traverse(obj);
      return found;
    };

    // Ne considérer que les fieldIds qui existent réellement dans au moins un élément
    const presentFieldIds = fieldIds.filter(fid => elements.some(elem => elementHasFieldId(elem, fid)));

    // Si aucun des fieldIds n'est présent dans les éléments, on les garde tous
    if (presentFieldIds.length === 0) {
      return elements;
    }

    const filteredElements = elements.filter(elem => {
      return presentFieldIds.every(fieldId => elementHasFieldId(elem, fieldId));
    });

    return filteredElements;
  }

  filterFormArrayWithCondition(actualFormArray, conditionToVerify) {
    return actualFormArray.controls.filter(iteration => this.verifyIfConditionIsOk(iteration, conditionToVerify));
  }

  verifyIfConditionIsOk(control, conditionToVerify) {
    // si les deux ont , comparer , ajouter dans form ou un nouveau form
    let canCreate = true;

    let conditionFromField = conditionToVerify.split('=')[1].replaceAll('\'', '').replace(']', '').replace(']', '');
    let splitedCondition = conditionToVerify.split('=')[0].replace('not(', '').split('[');
    let controlPath = splitedCondition[splitedCondition.length - 2].split('/');
    let controlItem = splitedCondition[splitedCondition.length - 1].replace('/@', '').replace('/', '');
    let isNot = conditionToVerify.includes('not(');
    let conditionFromControl;

    let tempControl = control;
    controlPath.forEach(element => {
      if (tempControl?.controls?.[element.toLowerCase()]) {
        tempControl = tempControl.controls[element.toLowerCase()];
      }
    });
    conditionFromControl = tempControl?.controls?.[controlItem.toLowerCase()]?.value?.val ?? tempControl?.controls?.[controlItem.toLowerCase()]?.value;
    if (conditionFromControl && conditionFromField.toString() === conditionFromControl.toString()) {
      canCreate = canCreate && !isNot;
    } else {
      canCreate = canCreate && isNot;
    }
    return canCreate;
  }

  /**
   * Vérifie les conditions de création avec les contrôles
   * Gère le cas où les deux paramètres sont des tableaux de conditions (chaînes)
   * ainsi que le cas où ce sont des objets avec des clés.
   */
  verifyConditionsCreationWithControls(control, conditionsCreation, conditionsToMatch) {
    if (!conditionsCreation || !conditionsToMatch) {
      return false;
    }

    // Cas 1: Les deux sont des tableaux de chaînes de conditions
    // Ex: ["[ParameterCode/&listName='number-threshold']"]
    if (Array.isArray(conditionsCreation) && Array.isArray(conditionsToMatch)) {
      // Vérifier que toutes les conditions à matcher sont présentes dans conditionsCreation
      // On extrait le listName pour une comparaison plus robuste
      const getListName = (condition: string): string | null => {
        const match = condition.match(/listName='([^']+)'/i);
        return match ? match[1].toLowerCase() : null;
      };

      const creationListNames = conditionsCreation.map(c => getListName(c)).filter(n => n !== null);
      const toMatchListNames = conditionsToMatch.map(c => getListName(c)).filter(n => n !== null);

      // Si on a des listNames, comparer par listName (plus robuste)
      if (creationListNames.length > 0 && toMatchListNames.length > 0) {
        return toMatchListNames.every(lnToMatch =>
          creationListNames.some(lnCreation => lnCreation === lnToMatch)
        );
      }

      // Sinon, comparer les chaînes directement (normaliser en minuscules et sans espaces)
      const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, '');
      const normalizedCreation = conditionsCreation.map(c => normalize(c));
      const normalizedToMatch = conditionsToMatch.map(c => normalize(c));

      return normalizedToMatch.every(cond =>
        normalizedCreation.some(cc => cc === cond || cc.includes(cond) || cond.includes(cc))
      );
    }

    // Cas 2: Ce sont des objets avec des clés (ancien comportement)
    let match = true;
    for (const key in conditionsToMatch) {
      if (conditionsToMatch.hasOwnProperty(key)) {
        const conditionValue = conditionsToMatch[key];
        const controlCondition = conditionsCreation?.[key];

        if (Array.isArray(conditionValue) && Array.isArray(controlCondition)) {
          // Comparer les tableaux de conditions
          const conditionMatch = conditionValue.every(cv =>
            controlCondition.some(cc => cc === cv || (cc && cv && cc.toString() === cv.toString()))
          );
          if (!conditionMatch) {
            match = false;
            break;
          }
        } else if (conditionValue !== controlCondition) {
          match = false;
          break;
        }
      }
    }

    return match;
  }


  /**
   * Récupère le noticeValue avec la condition
   */
  private getNoticeValueWithCondition(noticeValue, elementToVerify, conditionKey, repeatablePathWithIndex) {
    // Récupérer le chemin jusqu'à l'élément à vérifier
    let path = elementToVerify.jsonPath?.toLowerCase().split('.');
    let result = noticeValue;

    if (!path) return noticeValue;

    for (let i = 0; i < path.length; i++) {
      const key = path[i].replace('[]', '');
      if (result && result[key] !== undefined) {
        if (Array.isArray(result[key])) {
          const index = this.eformDataService.getArrayIndex(path[i], repeatablePathWithIndex);
          result = result[key][index >= 0 ? index : 0];
        } else {
          result = result[key];
        }
      } else {
        break;
      }
    }

    return result || noticeValue;
  }

  /**
   * Trouve l'index avec la sous-condition
   */
  findIndexWithSubCondition(subconditions, tempObj) {
    if (!Array.isArray(tempObj)) return 0;

    for (let i = 0; i < tempObj.length; i++) {
      let match = true;
      for (const sub of subconditions) {
        const condition = sub.content;
        if (condition && condition.includes('=')) {
          const parts = condition.split('=');
          const field = parts[0].replace(/.*\//, '').replace(/\[.*/, '').toLowerCase();
          const value = parts[1].replace(/'/g, '').replace(/\]/g, '');
          if (tempObj[i]?.[field]?.val !== value) {
            match = false;
            break;
          }
        }
      }
      if (match) return i;
    }

    return 0;
  }
}
